import { useApiBase } from './useApiBase'

/**
 * Acting / Impersonation status payload returned by the backend (#623).
 * Mirrors `ActingStatusBody` in lib/src/gennai/handlers/admin_acting.py.
 */
export interface ActingStatus {
  active: boolean
  sessionId?: string | null
  subjectUserId?: string | null
  subjectName?: string | null
  reason?: string | null
  startedAt?: number | null
  expiresAt?: number | null
}

export interface StartActingRequest {
  subjectUserId: string
  reason: string
}

export function useActing() {
  const { apiBase, authenticatedFetch } = useApiBase()
  const store = useActingStore()

  const _hydrate = (status: ActingStatus) => {
    if (status.active && status.sessionId && status.subjectUserId) {
      store.setSession({
        sessionId: status.sessionId,
        subjectUserId: status.subjectUserId,
        subjectName: status.subjectName ?? null,
        reason: status.reason ?? '',
        startedAt: status.startedAt ?? 0,
        expiresAt: status.expiresAt ?? 0,
      })
    }
    else {
      store.clear()
    }
  }

  const refreshStatus = async () => {
    const status = await authenticatedFetch<ActingStatus>(
      `${apiBase}/api/v1/admin/acting`,
      { method: 'GET' },
    )
    _hydrate(status)
    return status
  }

  const startActing = async (req: StartActingRequest) => {
    const status = await authenticatedFetch<ActingStatus>(
      `${apiBase}/api/v1/admin/acting`,
      { method: 'POST', body: req as unknown as Record<string, unknown> },
    )
    _hydrate(status)
    return status
  }

  const stopActing = async () => {
    const status = await authenticatedFetch<ActingStatus>(
      `${apiBase}/api/v1/admin/acting`,
      { method: 'DELETE' },
    )
    _hydrate(status)
    return status
  }

  return { refreshStatus, startActing, stopActing }
}
